/**
 * Sprache des Realtime-Führers (Alex 29.07.: „wir können ja die Sprache
 * einstellen und dann müsste der Realtime-Guide in der jeweiligen Sprache
 * starten").
 *
 * Ausgangslage: Das Gehirn (`tschauko-brain.generated.ts`) ist durchgehend
 * deutsch — Identität, Regeln, Stations-Index, Kern-Fakten. Es zu übersetzen
 * hieße, fünf Fassungen desselben Wissens zu pflegen, die auseinanderlaufen,
 * sobald ein Fakt sich ändert. Stattdessen bleibt das WISSEN deutsch und die
 * SPRACHE wird angewiesen: das Modell liest deutsch und spricht in der
 * gewählten Sprache. Dasselbe gilt für Werkzeug-Antworten, die ebenfalls
 * deutsch zurückkommen.
 *
 * Was diese Trennung NICHT kann: die vorproduzierten Erzählungen (Routen-
 * Intros, Stations-Narration, Annotations-Audio) sind fest eingesprochene
 * Aufnahmen und bleiben deutsch, egal was hier steht. Das betrifft nur den
 * live sprechenden Führer.
 */

export type AgentLanguage = 'de' | 'en' | 'sl' | 'it' | 'es'

/** Wie die Sprache im Systemtext benannt wird — in der Sprache selbst, damit
 *  das Modell nicht erst übersetzen muss, worum es geht. */
export const SPRACH_NAME: Record<AgentLanguage, string> = {
  de: 'Deutsch',
  en: 'English',
  sl: 'slovenščina',
  it: 'italiano',
  es: 'español',
}

/** Begrüßung je Sprache. Bewusst als feste Übersetzung und nicht dem Modell
 *  überlassen: der erste Satz ist der einzige, den JEDER Besucher hört, und
 *  eine Figur stellt sich nicht bei jedem Start anders vor. */
export const AGENT_GREETINGS: Record<AgentLanguage, string> = {
  de: 'Grüß Gott, ich bin Dr. Peter Tschauko. Was möchten Sie über die Tscheppaschlucht wissen?',
  en: "Good day, I am Dr. Peter Tschauko. What would you like to know about the Tscheppaschlucht gorge?",
  sl: 'Dober dan, sem dr. Peter Tschauko. Kaj bi radi izvedeli o soteski Tscheppaschlucht?',
  it: 'Buongiorno, sono il dottor Peter Tschauko. Che cosa desidera sapere sulla gola Tscheppaschlucht?',
  es: 'Buenos días, soy el doctor Peter Tschauko. ¿Qué le gustaría saber sobre el desfiladero de Tscheppaschlucht?',
}

/**
 * Sprach-Anweisung, die dem Gehirn vorangestellt wird. Nur für Nicht-Deutsch:
 * bei `de` bleibt der Systemtext unverändert (kein überflüssiger Block, der
 * bei jeder Sitzung Tokens kostet und nichts ändert).
 *
 * Die Anweisung steht VORNE, nicht hinten: was zuerst kommt, wirkt bei langen
 * Systemtexten zuverlässiger — und diese Regel überstimmt alles, was danach
 * auf Deutsch folgt.
 */
export function buildLanguageDirective(lang: AgentLanguage, eigenname = 'Dr. Peter Tschauko'): string {
  if (lang === 'de') return ''
  const name = SPRACH_NAME[lang]
  return [
    `=== SPRACHE (überstimmt alles Folgende) ===`,
    `Der Besucher hat ${name} eingestellt. Sprich AUSSCHLIESSLICH ${name} — jede Antwort, jede Begrüßung, jede Rückfrage.`,
    `Dein gesamtes Wissen unten ist auf Deutsch notiert, ebenso die Antworten deiner Werkzeuge. Das ist NUR die Notizsprache: lies deutsch, sprich ${name}.`,
    `Antwortet der Besucher auf Deutsch, bleibst du trotzdem bei ${name} — die Einstellung der App gilt, nicht die Sprache der letzten Frage.`,
    `EIGENNAMEN bleiben unverändert und werden NICHT übersetzt: Tscheppaschlucht, Tschaukofall, Tschaukofinger, Loiblbach, Bodental, Meerauge, Teufelsbrücke, Goldenes Bründl, Deutscher Peter, Ferlach, Karawanken, Kärnten, und dein eigener Name ${eigenname}.`,
    `Auch Pflanzen- und Tiernamen aus den Werkzeug-Antworten nennst du zuerst mit dem deutschen Namen, dann — wenn du ihn sicher weißt — den in ${name} gebräuchlichen. Rate keine Artnamen; im Zweifel nur den deutschen und den wissenschaftlichen.`,
    `Preise, Uhrzeiten und Entfernungen sprichst du in der ${name} üblichen Form.`,
    ``,
  ].join('\n')
}

/** Die eingestellte Sprache — aus demselben Speicher wie die Oberfläche.
 *
 *  Bewusst zum ZEITPUNKT DES AUFRUFS gelesen und nicht beim Bauen des
 *  Controllers: den gibt es nur einmal pro Seitenaufruf, die Sprache kann der
 *  Besucher jederzeit umstellen. Wer sie einmal einfriert, hat einen Führer,
 *  der bis zum Neuladen in der alten Sprache spricht. */
export function currentAgentLanguage(): AgentLanguage {
  try {
    const stored = localStorage.getItem('tschepp-lang')
    if (stored && stored in AGENT_GREETINGS) return stored as AgentLanguage
    const browser = navigator.language.split('-')[0]
    if (browser in AGENT_GREETINGS) return browser as AgentLanguage
  } catch { /* kein localStorage (SSR/Privatmodus) → Deutsch */ }
  return 'de'
}
